import React, { useState } from 'react';
import PropTypes from "prop-types";
import { useDispatch } from 'react-redux';
import { Modal, Button, ProgressIndicator, ErrorBanner } from "rhombus-react";

import { removeCreativesFromConcept } from 'reducers/ConceptDetails';
import { loadCreatives } from 'reducers/Creatives';
import { closeModal } from 'reducers/Modal';

import { pluralize } from 'utils/common';

import CreativesTable from 'widgets/table/CreativesTable';
import { STATUS_BAD_REQUEST } from 'utils/constants';

import "./RemoveCreativeFromConcept.css";

export function messageCreator(successMessage, failMessage) {
	return ({ data }) => {
		if (data.code >= STATUS_BAD_REQUEST) {
			return {
				id: new Date().getTime(),
				type: "error",
				messageHeader: "Error",
				message: failMessage,
			};
		}

		return {
			id: new Date().getTime(),
			type: "success",
			messageHeader: "Success",
			message: successMessage,
		};
	};
}

export default function RemoveCreativesFromConcept({ libraryId, creatives }) {
	const dispatch = useDispatch();
	const [isUpdating, setIsUpdating] = useState(false);
	const [error, setError] = useState("");
	const pluralizedCreative = pluralize("creative", creatives.length);

	const successMessage = `Successfully removed ${pluralizedCreative} from the concept`;
	const failMessage = `Failed to remove ${pluralizedCreative} from the concept`;
	const meta = { buildMessage: messageCreator(successMessage, failMessage) };

	function onClose() {
		dispatch(closeModal());
	}

	function onSubmit() {
		setIsUpdating(true);

		const creativeIds = creatives.map(crt => crt.id);
		dispatch(removeCreativesFromConcept(libraryId, creativeIds, { meta }))
			.then((resp) => {
				setIsUpdating(false);
				if (resp?.success) {
					onClose();
					dispatch(loadCreatives(libraryId));
					return;
				}

				setError("Could not remove creative");
			});
	}

	function renderContent() {
		let errorDisplay = <ErrorBanner title={error} shouldDisplay={Boolean(error)} />;

		return (
			<>
				<ProgressIndicator isLoading={isUpdating} />
				{errorDisplay}
				<div className="confirm-message">
					Are you sure you want to remove the following {pluralizedCreative} from the concept?
				</div>
				<CreativesTable creatives={creatives} shouldLinkPreview={false} />
			</>
		);
	}

	function renderFooter() {
		return (
			<>
				<Button theme="grey" disabled={isUpdating} onClick={onClose}>Cancel</Button>,
				<Button theme="blue" disabled={!creatives.length || isUpdating} onClick={onSubmit}>
					Remove {pluralize("Creative", creatives.length)}
				</Button>
			</>
		);
	}

	const bladeProps = {
		verticallyCenter: true,
	};

	return (
		<Modal
			title={`Remove ${pluralize("Creative", creatives.length)} from Concept`}
			type="modal"
			className="remove-creatives-from-concept-modal"
			xClose={true}
			allowFullScreen={false}
			bladeProps={bladeProps}
			renderContent={renderContent}
			renderFooter={renderFooter}
			onClose={onClose}
			setAppTitle={false}
		/>
	);
}

RemoveCreativesFromConcept.displayName = "RemoveCreativesFromConcept";
RemoveCreativesFromConcept.propTypes = {
	libraryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	creatives: PropTypes.arrayOf(PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	})).isRequired,
};
